import React, { FC, useState, useCallback, useRef, useEffect } from "react";
import { Box, Stack, Typography, styled, Modal as MuiModal, CircularProgress } from "@mui/material";
import { background, black, button, divider, text } from "@/theme/colors";
import { Button } from "@/components/elements/Button";
import { Input } from "@/components/elements/Input";
import { IconBox } from "@/components/elements/IconBox";
import { IconClose } from "@/assets/icons";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useSavedConditionList, useDeleteSavedCondition, useUpdateSavedCondition, useCopySavedCondition } from "@/api/services/main/saved-condition";
import { showError } from "@/utils/error";
import { toast } from "@/utils/toast";
import {
  TCaseMatchFilterFormExtended,
  defaultCaseMatchFilterValues,
  normalizeCaseMatchMultiSelectFields,
} from "@/features/case/components/CaseMatches/case-match-filter-shared";
import { CaseMatchAppliedConditionDetailPopover } from "@/features/case/components/CaseMatches/CaseMatchAppliedConditionDetailPopover";
import { CaseMatchEditSavedConditionModal } from "@/features/case/components/CaseMatches/CaseMatchEditSavedConditionModal";
import { SAVED_CONDITION_TITLE_MAX_LENGTH } from "@/constants/common";

export type TCaseMatchSavedCondition = {
  id: number;
  title: string;
  filters: TCaseMatchFilterFormExtended;
  createdAt: string;
};

/** Shared prop shape for condition detail popover components */
export type ConditionDetailPopoverProps = {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  filterValues: Record<string, string | string[]> | null;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onApply: (condition: TCaseMatchSavedCondition) => void;
  pageType: string;
  appliedConditionId?: number | null;
  onConditionTitleChange?: (id: number, newTitle: string) => void;
  /** Custom detail popover component to override default CaseMatch popover */
  DetailPopoverComponent?: React.ComponentType<ConditionDetailPopoverProps>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  EditModalComponent?: React.ComponentType<{ isOpen: boolean; onClose: () => void; onSave: (...args: any[]) => void | Promise<void>; initialTitle: string; initialFilters: any }>;
};

const MAX_CONDITIONS = 10;

export const CaseMatchSavedConditionsListModal: FC<Props> = ({ isOpen, onClose, onApply, pageType, appliedConditionId, onConditionTitleChange, DetailPopoverComponent, EditModalComponent }) => {
  const DetailPopover = DetailPopoverComponent ?? CaseMatchAppliedConditionDetailPopover;
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [copyingId, setCopyingId] = useState<number | null>(null);
  const [detailAnchorEl, setDetailAnchorEl] = useState<HTMLElement | null>(null);
  const [detailFilters, setDetailFilters] = useState<TCaseMatchFilterFormExtended | null>(null);
  const [editingCondition, setEditingCondition] = useState<TCaseMatchSavedCondition | null>(null);
  const [inlineEditingId, setInlineEditingId] = useState<number | null>(null);
  const [inlineEditValue, setInlineEditValue] = useState("");
  const inlineInputRef = useRef<HTMLInputElement>(null);
  const inlineEditReadyRef = useRef(false);

  const { data, isLoading } = useSavedConditionList(pageType);
  const deleteMutation = useDeleteSavedCondition();
  const updateMutation = useUpdateSavedCondition();
  const copyMutation = useCopySavedCondition();

  const conditions: TCaseMatchSavedCondition[] = (data?.data ?? []).map((item) => {
    const raw = (item.filters ?? {}) as Partial<Record<string, unknown>>;
    const filters = {
      ...defaultCaseMatchFilterValues,
      ...normalizeCaseMatchMultiSelectFields(raw),
    } as TCaseMatchFilterFormExtended;
    return { id: item.id, title: item.title, filters, createdAt: item.createdAt };
  });

  const handleDelete = useCallback(
    async (id: number) => {
      if (deletingId !== null) return;
      setDeletingId(id);
      try {
        await deleteMutation.mutateAsync(id);
        setSelectedIndex(null);
        toast.success("条件を削除しました。");
      } catch (e) {
        showError(e);
      } finally {
        setDeletingId(null);
      }
    },
    [deleteMutation, deletingId],
  );

  const handleCopy = useCallback(
    async (id: number) => {
      setCopyingId(id);
      try {
        const res = await copyMutation.mutateAsync(id);
        setInlineEditingId(res.data.id);
      } catch (e) {
        showError(e);
      } finally {
        setCopyingId(null);
      }
    },
    [copyMutation],
  );

  useEffect(() => {
    if (inlineEditingId === null) {
      inlineEditReadyRef.current = false;
      return;
    }
    const item = conditions.find((c) => c.id === inlineEditingId);
    if (item && !inlineEditReadyRef.current) {
      setInlineEditValue(item.title);
      setTimeout(() => {
        inlineInputRef.current?.focus();
        inlineInputRef.current?.select();
        inlineEditReadyRef.current = true;
      }, 100);
    }
  }, [inlineEditingId]);

  const handleInlineEditSave = useCallback(
    async (id: number) => {
      if (!inlineEditReadyRef.current) return;
      const trimmed = inlineEditValue.trim();
      if (!trimmed || trimmed.length > SAVED_CONDITION_TITLE_MAX_LENGTH) {
        setInlineEditingId(null);
        return;
      }
      const original = conditions.find((c) => c.id === id);
      if (original && trimmed !== original.title) {
        try {
          await updateMutation.mutateAsync({ id, payload: { title: trimmed } });
          if (appliedConditionId === id) onConditionTitleChange?.(id, trimmed);
        } catch (e) {
          showError(e);
        }
      }
      setInlineEditingId(null);
    },
    [inlineEditValue, conditions, updateMutation, appliedConditionId, onConditionTitleChange],
  );

  const handleEditSave = useCallback(
    async (title: string, filters: TCaseMatchFilterFormExtended) => {
      if (!editingCondition) return;
      try {
        await updateMutation.mutateAsync({
          id: editingCondition.id,
          payload: { title, filters: filters as unknown as Record<string, unknown> },
        });
        if (appliedConditionId === editingCondition.id) onConditionTitleChange?.(editingCondition.id, title);
        toast.success("条件を更新しました。");
      } catch (e) {
        showError(e);
      }
    },
    [editingCondition, updateMutation, appliedConditionId, onConditionTitleChange],
  );

  const handleApply = useCallback(() => {
    if (selectedIndex === null) return;
    const condition = conditions[selectedIndex];
    if (condition) {
      onApply(condition);
      onClose();
    }
  }, [selectedIndex, conditions, onApply, onClose]);

  const handleModalClose = useCallback(() => {
    setSelectedIndex(null);
    onClose();
  }, [onClose]);

  return (
    <MuiModal
      open={isOpen}
      onClose={(_event, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") return;
        handleModalClose();
      }}
      sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
    >
      <ModalContainer>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="head20Bold">保存した条件一覧</Typography>
          <IconBox onClick={handleModalClose} sx={{ p: 0 }}>
            <IconClose color={black[80]} fontSize={24} />
          </IconBox>
        </Stack>
        <Typography variant="cap12" sx={{ color: text.secondary, mt: 0.5 }}>
          保存した条件は{MAX_CONDITIONS}件まで保存できます。
        </Typography>

        <TableContainer sx={{ mt: 2 }}>
          <TableHeader>
            <Typography variant="body14Semi">タイトル名</Typography>
          </TableHeader>

          {isLoading ? (
            <EmptyState>
              <CircularProgress size={24} />
            </EmptyState>
          ) : conditions.length === 0 ? (
            <EmptyState>
              <Typography variant="body14" sx={{ color: text.tertiary }} fontWeight={700}>
                データがありません。
              </Typography>
            </EmptyState>
          ) : (
            conditions.map((condition, index) => (
              <TableRow key={condition.id} isSelected={selectedIndex === index} onClick={() => setSelectedIndex(index)}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ width: "100%" }}>
                  <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
                    {inlineEditingId === condition.id ? (
                      <Input
                        inputRef={inlineInputRef}
                        value={inlineEditValue}
                        onChange={(e) => setInlineEditValue(e.target.value)}
                        onBlur={() => handleInlineEditSave(condition.id)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handleInlineEditSave(condition.id);
                          }
                          if (e.key === "Escape") setInlineEditingId(null);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        size="md"
                        nonControl
                        placeholder="タイトルを入力"
                        sx={{ width: "80%" }}
                      />
                    ) : (
                      <Typography variant="body14">{condition.title}</Typography>
                    )}
                    <Box
                      component="span"
                      onClick={(e: React.MouseEvent<HTMLElement>) => {
                        e.stopPropagation();
                        setDetailAnchorEl(e.currentTarget);
                        setDetailFilters(condition.filters);
                      }}
                      sx={{ display: "inline-flex", alignItems: "center", cursor: "pointer", flexShrink: 0 }}
                    >
                      <InfoOutlinedIcon sx={{ color: text.primary, fontSize: 16 }} />
                    </Box>
                  </Stack>
                  <Stack direction="row" spacing={1}>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingCondition(condition);
                      }}
                    >
                      編集
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      isLoading={copyingId === condition.id}
                      onClick={(e) => {
                        e.stopPropagation();
                        return handleCopy(condition.id);
                      }}
                    >
                      条件をコピー
                    </Button>
                    <Button
                      type="button"
                      variant="danger"
                      size="sm"
                      isLoading={deletingId === condition.id}
                      onClick={(e) => {
                        e.stopPropagation();
                        return handleDelete(condition.id);
                      }}
                    >
                      削除
                    </Button>
                  </Stack>
                </Stack>
              </TableRow>
            ))
          )}
        </TableContainer>

        <Stack direction="row" justifyContent="center" spacing={2} sx={{ mt: 3 }}>
          <Button type="button" variant="ghost" onClick={handleModalClose} size="sm">
            キャンセル
          </Button>
          <Button type="button" onClick={handleApply} size="sm" disabled={selectedIndex === null}>
            適用する
          </Button>
        </Stack>

        <DetailPopover
          anchorEl={detailAnchorEl}
          onClose={() => {
            setDetailAnchorEl(null);
            setDetailFilters(null);
          }}
          filterValues={detailFilters}
        />

        {editingCondition && (
          EditModalComponent ? (
            <EditModalComponent
              isOpen={Boolean(editingCondition)}
              onClose={() => setEditingCondition(null)}
              onSave={handleEditSave}
              initialTitle={editingCondition.title}
              initialFilters={editingCondition.filters}
            />
          ) : (
            <CaseMatchEditSavedConditionModal
              isOpen={Boolean(editingCondition)}
              onClose={() => setEditingCondition(null)}
              onSave={handleEditSave}
              initialTitle={editingCondition.title}
              initialFilters={editingCondition.filters}
              />
          )
        )}
      </ModalContainer>
    </MuiModal>
  );
};

const ModalContainer = styled(Box)`
  width: 720px;
  max-width: 90vw;
  max-height: 90vh;
  overflow-y: auto;
  background-color: ${background.white};
  box-shadow: 0px 8px 24px 0px #00000026;
  border-radius: 8px;
  padding: 24px;
  outline: none;
  &:focus {
    outline: none;
  }
`;

const TableContainer = styled(Box)`
  border: 1px solid ${divider.middle};
  border-radius: 8px;
  overflow: hidden;
`;

const TableHeader = styled(Box)`
  padding: 12px 16px;
  background-color: ${background.primary};
  border-bottom: 1px solid ${divider.middle};
`;

const EmptyState = styled(Box)`
  padding: 24px;
  text-align: center;
`;

const TableRow = styled(Box, { shouldForwardProp: (prop) => prop !== "isSelected" })<{ isSelected: boolean }>`
  display: flex;
  align-items: center;
  padding: 10px 16px;
  cursor: pointer;
  background-color: ${({ isSelected }) => (isSelected ? button.hover_02 : background.white)};
  border-bottom: 1px solid ${divider.middle};
  transition: background-color 0.15s ease;
  &:last-child {
    border-bottom: none;
  }
  &:hover {
    background-color: ${({ isSelected }) => (isSelected ? button.hover_02 : divider.low)};
  }
`;
